package com.dotdrives.client

import com.dotdrives.config.BuildKonfig
import com.dotdrives.model.AuthProvider
import com.dotdrives.model.AuthSessionSnapshot
import com.dotdrives.model.AuthSessionSource
import com.dotdrives.model.SessionInfo
import com.dotdrives.model.SignUpOutcome
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.OtpType
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.providers.Azure
import io.github.jan.supabase.auth.providers.Google
import io.github.jan.supabase.auth.providers.builtin.Email
import io.github.jan.supabase.auth.providers.builtin.OTP
import io.github.jan.supabase.auth.status.SessionStatus
import io.github.jan.supabase.auth.status.SessionSource
import io.github.jan.supabase.auth.user.UserInfo
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

class AuthClient(
    private val client: SupabaseClient = SupabaseProvider.client,
) {
    val session: Flow<SessionInfo?> = client.auth.sessionStatus
        .map { status ->
            (status as? SessionStatus.Authenticated)
                ?.session
                ?.user
                ?.toSessionInfo()
        }
        .distinctUntilChanged()

    suspend fun signIn(email: String, password: String): SessionInfo {
        client.auth.signInWith(Email) {
            this.email = email
            this.password = password
        }
        return requireNotNull(currentSession()) {
            "Supabase did not establish a session"
        }
    }

    suspend fun signUp(email: String, password: String, redirectUrl: String? = null): SignUpOutcome {
        val user = client.auth.signUpWith(Email, redirectUrl = redirectUrl) {
            this.email = email
            this.password = password
        } ?: client.auth.currentUserOrNull()

        requireNotNull(user) {
            "Supabase did not return the signed-up user"
        }
        return client.auth.currentSessionOrNull()
            ?.user
            ?.toSessionInfo()
            ?.let(SignUpOutcome::SignedIn)
            ?: SignUpOutcome.ConfirmationRequired
    }

    suspend fun signInWithProvider(provider: AuthProvider, redirectUrl: String) {
        val httpClient = HttpClient()
        val settings = try {
            httpClient.get(
                "${BuildKonfig.SUPABASE_URL.trimEnd('/')}/auth/v1/settings",
            ) {
                header("apikey", BuildKonfig.SUPABASE_PUBLISHABLE_KEY)
            }.body<String>()
        } finally {
            httpClient.close()
        }
        check(providerEnabled(settings, provider)) {
            "${provider.displayName} sign-in is not configured yet."
        }
        when (provider) {
            AuthProvider.Google -> client.auth.signInWith(Google, redirectUrl = redirectUrl)
            AuthProvider.Microsoft -> client.auth.signInWith(Azure, redirectUrl = redirectUrl)
        }
    }

    suspend fun sendMagicLink(email: String, redirectUrl: String) {
        client.auth.signInWith(OTP, redirectUrl = redirectUrl) {
            this.email = email
            createUser = false
        }
    }

    suspend fun sendPasswordReset(email: String, redirectUrl: String) {
        client.auth.resetPasswordForEmail(email, redirectUrl = redirectUrl)
    }

    suspend fun verifyRecoveryToken(tokenHash: String): SessionInfo {
        client.auth.verifyEmailOtp(
            type = OtpType.Email.RECOVERY,
            tokenHash = tokenHash,
        )
        return requireNotNull(currentSession()) {
            "Supabase did not establish a recovery session"
        }
    }

    suspend fun updatePassword(newPassword: String) {
        client.auth.updateUser {
            password = newPassword
        }
    }

    suspend fun signOut() {
        client.auth.signOut()
    }

    suspend fun awaitInitialSession(): AuthSessionSnapshot {
        val status = client.auth.sessionStatus.first {
            it !is SessionStatus.Initializing
        }
        val authenticated = status as? SessionStatus.Authenticated
        return AuthSessionSnapshot(
            session = authenticated?.session?.user?.toSessionInfo(),
            source = if (authenticated?.source == SessionSource.External) {
                AuthSessionSource.External
            } else {
                AuthSessionSource.Other
            },
        )
    }

    fun currentSession(): SessionInfo? =
        client.auth.currentUserOrNull()?.toSessionInfo()

    fun requireCurrentUserId(): String =
        requireNotNull(client.auth.currentUserOrNull()?.id) {
            "An authenticated user is required"
        }
}

private val AuthProvider.settingsKey: String
    get() = when (this) {
        AuthProvider.Google -> "google"
        AuthProvider.Microsoft -> "azure"
    }

private val AuthProvider.displayName: String
    get() = when (this) {
        AuthProvider.Google -> "Google"
        AuthProvider.Microsoft -> "Microsoft"
    }

internal fun providerEnabled(settings: String, provider: AuthProvider): Boolean =
    Json.parseToJsonElement(settings)
        .jsonObject["external"]
        ?.jsonObject
        ?.get(provider.settingsKey)
        ?.jsonPrimitive
        ?.booleanOrNull == true

private fun UserInfo.toSessionInfo(): SessionInfo =
    SessionInfo(
        userId = id,
        email = email.orEmpty(),
    )
